import { Button, Input, Popconfirm, Spin } from 'antd'
import { RiskBadge } from 'features/fde'
import { Check, Edit3, Plus, Trash2, X } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoComponentDeficiency } from 'types'

import { CreateDeficiencyForm } from './components/CreateDeficiencyForm'
import { useDeficienciesWidget } from './hooks/useDeficienciesWidget'
import { DeficienciesWidgetProps } from './types/widgetTypes'

const WidgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #333333;
  border-radius: 0.5rem;
  padding: 0.75rem;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
`

const ViewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 0.25rem;
`

const ViewTitle = styled.h3`
  font-weight: 600;
  font-size: 1rem;
  color: ${({ theme }) => theme.forecast.text1};
  margin: 0;
`

const DeficienciesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1.5rem;
  text-align: center;
`

const EmptyStateText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  margin-bottom: 1rem;
`

const DeficiencyRow = styled.div<{ $selected?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.375rem;
  border: 0.0625rem solid
    ${({ $selected }) => ($selected ? '#ffffff' : 'transparent')};
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const DeficiencyInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const DeficiencyTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.text1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DeficiencyMeta = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
`

const EditingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.5rem;
  align-items: center;
`

interface DeficiencyRowData {
  id: string
  title: string
  description?: string
  componentId?: string
  componentName?: string
  likelihood?: number
  consequence?: number
  financialImpact?: number
  riskScore?: number
  deficiency: GeckoComponentDeficiency
}

export const DeficienciesWidget: React.FC<DeficienciesWidgetProps> = ({
  unit,
  site,
  riskAssessmentId,
  readonly = false,
  onDeficiencyCreated,
  onRiskClick,
  className,
  deficiencies,
  components,
  riskAssessmentConfig,
  isLoading,
  forceRefresh,
}) => {
  const {
    canEdit,
    canView,
    selectedDeficiencyId,
    setSelectedDeficiencyId,
    createDeficiency,
    editDeficiency,
    deleteDeficiency,
  } = useDeficienciesWidget({
    unit,
    site,
    riskAssessmentId,
    onDeficiencyCreated,
    deficiencies,
    components,
    forceRefresh,
  })

  const [editingDeficiencyId, setEditingDeficiencyId] = useState<string | null>(
    null
  )
  const [editingTitle, setEditingTitle] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)

  const effectiveReadonly = readonly || !canEdit

  const deficiencyRows: DeficiencyRowData[] = useMemo(() => {
    const sortedDeficiencies = [...deficiencies].sort((a, b) => {
      if (a.created_at && b.created_at) {
        return b.created_at.localeCompare(a.created_at)
      }
      return b.id.localeCompare(a.id)
    })

    return sortedDeficiencies.map((deficiency) => {
      const componentId = deficiency.tags.component
      const component = componentId
        ? components.find((c) => c.id === componentId)
        : undefined

      return {
        id: deficiency.id,
        title: deficiency.data.title || 'Untitled Deficiency',
        description: deficiency.data.description,
        componentId,
        componentName: component?.data.name,
        likelihood: deficiency.data.likelihood,
        consequence: deficiency.data.consequence,
        financialImpact: deficiency.data.financialImpact,
        riskScore: deficiency.data.riskScore,
        deficiency,
      }
    })
  }, [deficiencies, components])

  const handleDeficiencyClick = (deficiencyId: string) => {
    if (selectedDeficiencyId === deficiencyId) {
      setSelectedDeficiencyId(undefined)
    } else {
      setSelectedDeficiencyId(deficiencyId)
    }
  }

  const handleEditClick = (
    e: React.MouseEvent,
    deficiencyId: string,
    currentTitle: string
  ) => {
    e.stopPropagation()
    setEditingDeficiencyId(deficiencyId)
    setEditingTitle(currentTitle)
  }

  const handleSaveEdit = async (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (editingDeficiencyId && editingTitle.trim()) {
      await editDeficiency(editingDeficiencyId, {
        title: editingTitle.trim(),
      })
      setEditingDeficiencyId(null)
      setEditingTitle('')
    }
  }

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingDeficiencyId(null)
    setEditingTitle('')
  }

  const handleRiskClick = (
    e: React.MouseEvent | undefined,
    deficiency: DeficiencyRowData
  ) => {
    e?.stopPropagation()
    if (!effectiveReadonly && onRiskClick) {
      onRiskClick(deficiency.deficiency)
    }
  }

  const handleCreateDeficiency = async (data: {
    title: string
    description?: string
    componentId?: string
  }) => {
    await createDeficiency(data)
    setShowCreateForm(false)
  }

  if (!canView) {
    return null
  }

  if (isLoading) {
    return (
      <WidgetContainer className={className}>
        <LoadingContainer>
          <Spin />
        </LoadingContainer>
      </WidgetContainer>
    )
  }

  return (
    <WidgetContainer className={className}>
      <ViewHeader>
        <ViewTitle>Deficiencies ({deficiencies.length})</ViewTitle>
        {!effectiveReadonly && (
          <Button
            type='primary'
            size='small'
            onClick={() => setShowCreateForm(!showCreateForm)}
            icon={showCreateForm ? <X size={14} /> : <Plus size={14} />}>
            {showCreateForm ? 'Cancel' : 'Add Deficiency'}
          </Button>
        )}
      </ViewHeader>

      {showCreateForm && (
        <CreateDeficiencyForm
          components={components}
          onSubmit={handleCreateDeficiency}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      <DeficienciesContainer>
        {deficiencyRows.length === 0 && !showCreateForm ? (
          <EmptyStateContainer>
            <EmptyStateText>No deficiencies recorded yet</EmptyStateText>
          </EmptyStateContainer>
        ) : (
          deficiencyRows.map((row) => (
            <DeficiencyRow
              key={row.id}
              $selected={selectedDeficiencyId === row.id}
              onClick={() => handleDeficiencyClick(row.id)}>
              {editingDeficiencyId === row.id ? (
                <EditingRow>
                  <Input
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onPressEnter={() => handleSaveEdit()}
                    onClick={(e) => e.stopPropagation()}
                    size='small'
                  />
                  <Button
                    size='small'
                    type='text'
                    icon={<Check size={12} />}
                    onClick={handleSaveEdit}
                    disabled={!editingTitle.trim()}
                  />
                  <Button
                    size='small'
                    type='text'
                    icon={<X size={12} />}
                    onClick={handleCancelEdit}
                  />
                </EditingRow>
              ) : (
                <>
                  <DeficiencyInfo>
                    <DeficiencyTitle>{row.title}</DeficiencyTitle>
                    {row.description && (
                      <DeficiencyMeta>{row.description}</DeficiencyMeta>
                    )}
                    {row.componentName && (
                      <DeficiencyMeta>
                        Component: {row.componentName}
                      </DeficiencyMeta>
                    )}
                    {row.likelihood !== undefined && row.likelihood > 0 && (
                      <DeficiencyMeta>
                        Likelihood:{' '}
                        {riskAssessmentConfig?.data.likelihoodLabels?.[
                          row.likelihood - 1
                        ]?.description ?? row.likelihood}
                      </DeficiencyMeta>
                    )}
                    {row.financialImpact !== undefined &&
                      row.financialImpact > 0 && (
                        <DeficiencyMeta>
                          Eval. Rationale: $
                          {row.financialImpact.toLocaleString()}
                        </DeficiencyMeta>
                      )}
                  </DeficiencyInfo>
                  <RiskBadge
                    riskValue={row.riskScore}
                    riskAssessmentConfig={riskAssessmentConfig}
                    onClick={(e) => handleRiskClick(e, row)}
                  />
                  {!effectiveReadonly && (
                    <ActionButtons>
                      <Button
                        size='small'
                        type='text'
                        icon={<Edit3 size={12} />}
                        onClick={(e) => handleEditClick(e, row.id, row.title)}
                      />
                      <Popconfirm
                        title='Delete deficiency?'
                        description='This cannot be undone.'
                        okText='Delete'
                        cancelText='Cancel'
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteDeficiency(row.id)}
                        onPopupClick={(e) => e.stopPropagation()}>
                        <Button
                          size='small'
                          type='text'
                          icon={<Trash2 size={12} />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </ActionButtons>
                  )}
                </>
              )}
            </DeficiencyRow>
          ))
        )}
      </DeficienciesContainer>
    </WidgetContainer>
  )
}
